import { generateId } from "@msuite/utils/strings";
import React, { useState } from "react";
import {
	Bar,
	Brush,
	Cell,
	BarChart as RechartsBarChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { cn } from "../../utils";
import { ChartWrapper, type ChartWrapperProps } from "../chart-wrapper";
import { CartesianGrid } from "../components/cartesian-grid";
import { LinearGradient } from "../components/linear-gradient";
import { TooltipContent } from "../components/tooltip-content";
import { CHART_RADIUS } from "../constants";
import { findMinMaxValues } from "../handlers";
import { type ChartData, type ChartDataProps, colorClasses, type DataKey } from "../types";

/** Props Interface */
interface BarChartProps<T extends ChartData>
	extends Omit<ChartWrapperProps, "children" | "isLoading">,
		ChartDataProps<T> {
	withBrush?: boolean;
	displayKeyMap: Record<DataKey<T>, string>;
	formatter?: (item: number | undefined) => React.ReactNode | undefined;
	xTickFormatter?: (item: T) => string;
	xHidden?: boolean;
	isStack?: boolean;
}

export function BarChart<T extends { name: string }>({
	withBrush,
	data,
	dataKeys,
	formatter,
	displayKeyMap,
	xTickFormatter,
	xHidden,
	dataKeyClasses,
	isStack,
}: BarChartProps<T>) {
	/** Hooks */
	const { min, max } = findMinMaxValues(data ?? [], dataKeys);

	const [stackId] = useState(generateId());

	/** Render */
	return (
		<ChartWrapper isLoading={!data}>
			<ResponsiveContainer
				width="100%"
				height="100%"
				className="relative"
			>
				<RechartsBarChart
					data={data}
					margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
					barCategoryGap="5%"
					barGap="5%"
				>
					<CartesianGrid />
					<YAxis
						domain={[min, max]}
						allowDataOverflow={false}
						tickLine={false}
						axisLine={false}
						className="text-xs"
						width={40}
						hide
					/>
					<Tooltip
						cursor={{
							className: "fill-primaryGray-500/10",
						}}
						content={(props) => {
							return (
								<TooltipContent
									payload={props.payload}
									label={props.label}
									displayKeyMap={displayKeyMap}
									formatter={formatter}
								/>
							);
						}}
						animationDuration={100}
						animationEasing="ease-out"
					/>
					<XAxis
						interval={0}
						dataKey="name"
						tickLine={false}
						axisLine={false}
						tickFormatter={
							xTickFormatter
								? (tickValue) => {
										const item = data?.find((d) => d.name === tickValue);
										return item ? xTickFormatter(item) : tickValue;
									}
								: undefined
						}
						fontSize={10}
						hide={xHidden}
					/>

					<defs>
						<LinearGradient id={"colorUv"} />
					</defs>
					{dataKeys.map((key, index) => {
						/** Render */
						return (
							<Bar
								key={`${key as string}-${index}`}
								dataKey={key as string}
								radius={CHART_RADIUS}
								animationBegin={0}
								stackId={isStack ? stackId : undefined}
							>
								{data?.map((entry, index2) => {
									const isNegative = (entry[key as keyof typeof entry] as number) < 0;

									/** Render */
									return (
										<Cell
											key={`${entry.name}-${index2}`}
											className={cn(
												"hover:opacity-90 cursor-pointer",
												isNegative && "fill-rose-500!",
												dataKeyClasses
													? colorClasses({
															color: dataKeyClasses[key],
														})
													: "",
											)}
											fill="url(#colorUv)"
											radius={CHART_RADIUS}
											style={{
												filter: !dataKeyClasses ? `brightness(${1 + index / 3.5})` : "",
											}}
										/>
									);
								})}
							</Bar>
						);
					})}
					{withBrush && (
						<Brush
							dataKey="name"
							height={10}
							stroke="var(--color-chart-base-1)"
							fill="rgba(0,0,0,0)"
							className="text-xs"
						/>
					)}
				</RechartsBarChart>
			</ResponsiveContainer>
		</ChartWrapper>
	);
}
